import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import AppDataTable from '../AppDataTable.js';
import { Fetch } from '../../utils/Utils.js';
import '../../weight/lots/WeightLots.css';
import '../../herd/ListHerd.css';
import './ListLotAnimals.css';

const LOT_PREFIX_KEY = {
  reproduction: 'lot_prefix',
  weight:       'weight_lot_prefix',
  herd:         'herd_lot_prefix',
};

const ANIMALS_URL = {
  reproduction: 'api/reproduction/animals',
};

const formatLotId = (lotId, prefix) => {
  if (!lotId && lotId !== 0) return '-';
  return `${prefix}-${String(lotId).padStart(4, '0')}`;
};

const ListLotAnimals = ({
  lotType,
  extraColumns = [],
  expandedRowComponent,
  initialLotFilter,
  tagPath,
  forceRefresh,
  customStyles,
  // mobileList(data) renders the list at phone width instead of the table
  // (e.g. ReproductionCardList); filters and data loading stay shared.
  mobileList,
}) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [lots, setLots] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [expandedRowIds, setExpandedRowIds] = useState(new Set());
  const [lotFilter, setLotFilter] = useState(initialLotFilter ? String(initialLotFilter) : '');
  const [tagFilter, setTagFilter] = useState('');

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    Fetch(`api/data/lots?search_fields=type,status&search_values=${lotType},active`, 'get', null, (d) => {
      setLots([...(d || [])].sort((a, b) => b.lot_id - a.lot_id));
    });
  }, [lotType]);

  useEffect(() => {
    if (initialLotFilter != null) setLotFilter(String(initialLotFilter));
  }, [initialLotFilter]);

  const animalsUrl = ANIMALS_URL[lotType];

  // See ListHerdAnimals.js's identical fetchSeq comment — every keystroke
  // fires a new request with no debounce, so a slow early response can
  // resolve after a fast later one and overwrite it with stale results.
  // fetchSeq ensures only the most recently *issued* request's response can
  // update state.
  const fetchSeq = useRef(0);
  const fetchAnimals = useCallback(() => {
    const params = [];
    if (lotFilter) params.push(`lot_id=${lotFilter}`);
    if (tagFilter) params.push(`tag=${tagFilter}`);
    const url = animalsUrl + (params.length ? '?' + params.join('&') : '');
    const seq = ++fetchSeq.current;
    Fetch(url, 'get', null,
      (d) => { if (seq === fetchSeq.current) setData(d || []); },
      () => { if (seq === fetchSeq.current) setData([]); }
    );
  }, [animalsUrl, lotFilter, tagFilter]);

  useEffect(() => { fetchAnimals(); }, [fetchAnimals]);

  useEffect(() => {
    if (forceRefresh) fetchAnimals();
  }, [forceRefresh, fetchAnimals]);

  const handleRowExpandToggled = (expanded, row) => {
    setExpandedRowIds(prev => {
      const next = new Set(prev);
      expanded ? next.add(row._id) : next.delete(row._id);
      return next;
    });
  };

  const activeFilterCount = [lotFilter].filter(Boolean).length;

  const resetFilters = () => setLotFilter('');

  const LotSelect = ({ value, onChange }) => (
    <select value={value} onChange={onChange}>
      <option value="">{t('all_lots')}</option>
      {lots.map(lot => (
        <option key={lot._id} value={lot.lot_id}>
          {formatLotId(lot.lot_id, t(LOT_PREFIX_KEY[lotType] || 'lot_prefix'))}
          {lot.name ? ` — ${lot.name}` : ''}
        </option>
      ))}
    </select>
  );

  const columns = [
    {
      name: t('tag'),
      selector: row => row.tag,
      sortable: true,
      cell: row => tagPath
        ? <span className="wl-lot-link" onClick={() => window.location.href = `${tagPath}/${row.tag}`}>{row.tag}</span>
        : row.tag,
    },
    {
      name: t('lot'),
      selector: row => row.lot_id,
      sortable: true,
      cell: row => formatLotId(row.lot_id, t(LOT_PREFIX_KEY[lotType] || 'lot_prefix')),
    },
    ...extraColumns,
  ];

  return (
    <div className="ListLotAnimals">
      {isMobile ? (
        <div className="mobile-search-filter">
          <input
            className="mobile-search-input"
            type="text"
            placeholder={t('find_tag')}
            onChange={e => setTagFilter(e.target.value)}
          />
          <button
            className={`mobile-filter-toggle ${filtersOpen ? 'open' : ''} ${activeFilterCount > 0 ? 'active' : ''}`}
            onClick={() => setFiltersOpen(o => !o)}
          >
            {t('filters')}
            {activeFilterCount > 0 && <span className="mobile-filter-badge">{activeFilterCount}</span>}
          </button>
        </div>
      ) : (
        <div className="desktop-filter-bar">
          <div className="desktop-filter-top">
            <input
              type="text"
              className="desktop-search-input"
              placeholder={t('find_tag')}
              onChange={e => setTagFilter(e.target.value)}
            />
            <LotSelect value={lotFilter} onChange={e => setLotFilter(e.target.value)} />
          </div>
        </div>
      )}

      {isMobile && lotFilter && (
        <div className="lot-filter-chips">
          <button className="lot-filter-chip" onClick={resetFilters} aria-label={t('clear_filter')}
            data-testid="lot-filter-chip">
            {formatLotId(Number(lotFilter), t(LOT_PREFIX_KEY[lotType] || 'lot_prefix'))} ✕
          </button>
        </div>
      )}

      {isMobile && mobileList ? mobileList(data) : (
      <AppDataTable
        defaultSortFieldId={1}
        defaultSortAsc={true}
        data={data}
        columns={columns}
        keyField="_id"
        expandableRows={!!expandedRowComponent}
        expandableRowsComponent={expandedRowComponent}
        expandableRowExpanded={row => expandedRowIds.has(row._id)}
        onRowExpandToggled={handleRowExpandToggled}
        customStyles={customStyles}
      />
      )}

      {filtersOpen && (
        <>
          <div className="filter-sheet-backdrop" onClick={() => setFiltersOpen(false)} />
          <div className="filter-sheet">
            <div className="filter-sheet-handle" />
            <div className="filter-sheet-header">
              <span className="filter-sheet-title">{t('filters')}</span>
              <button className="filter-sheet-close" onClick={() => setFiltersOpen(false)}>✕</button>
            </div>
            <div className="filter-sheet-body">
              <LotSelect value={lotFilter} onChange={e => setLotFilter(e.target.value)} />
            </div>
            <div className="filter-sheet-footer">
              <button
                className="filter-sheet-reset"
                onClick={resetFilters}
                disabled={activeFilterCount === 0}
              >
                {t('reset_filters')} {activeFilterCount > 0 && `(${activeFilterCount})`}
              </button>
              <button className="filter-sheet-apply" onClick={() => setFiltersOpen(false)}>
                {t('done')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ListLotAnimals;
